import { Tab } from "@headlessui/react";
import React, { useState } from "react";
import { Option } from "@/models/sorting-filtering";
import {
  Beat,
  IdentifiableOption,
  SoundKit,
  ViewObject,
} from "@/models/models";
import TitleBarMobile from "../modals/titlebar-mobile";
import DropdownAlt from "../dropdown/dropdown-alt";
import ToggleButton from "../buttons/toggle-button";
import { useWindowSize } from "@/src/hooks";
import ResponsiveList from "../responsive-list";
import ListTile from "../tiles/list-tile";
import { isSoundkit } from "@/lib/helpers";
import { AudioType } from "@/models/enums";
import ResponsiveGrid from "../responsive-grid";
import GridTileSkeleton from "../skeletons/grid-tile-skeleton";
import ListTileSkeleton from "../skeletons/list-tile-skeleton";
import GridTile from "../tiles/grid-tile";
import { ClassProps } from "@/lib/types/class-props";
import Skeleton from "../skeleton";
import Empty from "../empty/empty-results";
import Headphones from "../icons/lib/headphones";
import { cx } from "@/lib/utils/className.utils";
import { tabs } from "./tabs";
import { sortOptions } from "./sortOptions";
import { useAudioPlayer } from "@/src/contexts/AudioPlayerContext";
import useViewType from "@/lib/hooks/useViewType";
import { Entity } from "@/lib/types/entity.type";

export type CreatorsAsssetsTabsProps = {
  /**
   * Indicates if the general component should display all skeletons.
   */
  loading?: boolean | null;
  beatsCount?: number | null;
  songsCount?: number | null;
  soundkitsCount?: number | null;
  beats?: Beat[] | null;
  songs?: Beat[] | null;
  soundkits?: SoundKit[] | null;
  isBeatsLoading?: boolean | null;
  isSongsLoading?: boolean | null;
  isSoundkitsLoading?: boolean | null;
  isBeatsLoadingMore?: boolean | null;
  isSongsLoadingMore?: boolean | null;
  isSoundkitsLoadingMore?: boolean | null;
  selectedTab: IdentifiableOption;
  onSortChange?: (sortCriteria: IdentifiableOption) => void;
  onTabChange: (tab: IdentifiableOption) => void;
  onViewMore?: (category: Entity) => void;
} & ClassProps;

const CreatorsAssetsTabs = ({
  loading,
  beatsCount,
  songsCount,
  soundkitsCount,
  beats,
  songs,
  soundkits,
  isBeatsLoading,
  isSongsLoading,
  isSoundkitsLoading,
  isBeatsLoadingMore,
  isSongsLoadingMore,
  isSoundkitsLoadingMore,
  selectedTab,
  onSortChange,
  onTabChange,
  onViewMore,
  className,
}: CreatorsAsssetsTabsProps) => {
  const size = useWindowSize();
  const { viewType, toggleViewType } = useViewType();
  const { setCurrentPlaylist } = useAudioPlayer();

  const [sort, setSort] = useState<Option>(sortOptions[0]);

  const handleSortClick = (sortCriteria: IdentifiableOption) => {
    const { name: criteria } = sortCriteria;
    if (criteria === sort.name) {
      return;
    }

    setSort(sortCriteria);
    onSortChange?.(sortCriteria);
  };

  const handleItemPlay = (item: ViewObject) => {
    let entities: ViewObject[] = [];

    switch (selectedTab.id) {
      case 0:
        entities = beats ?? [];
        break;
      case 1:
        entities = songs ?? [];
        break;
      case 2:
        entities = soundkits ?? [];
        break;
      default:
        break;
    }

    setCurrentPlaylist((currentPlaylist) => {
      if (currentPlaylist.find((playlistItem) => playlistItem.id === item.id)) {
        return currentPlaylist;
      }
      return entities as ViewObject[];
    });
  };

  if (loading) {
    return (
      <div className={cx("flex flex-col gap-3", className)}>
        <div className="flex flex-row gap-5">
          <Skeleton className="w-[150px] h-[64px]" />
          <Skeleton className="w-[150px] h-[64px]" />
          <Skeleton className="w-[150px] h-[64px]" />
        </div>
        <ResponsiveGrid loading={true}></ResponsiveGrid>
      </div>
    );
  }

  if (
    beatsCount === 0 &&
    !isBeatsLoading &&
    songsCount === 0 &&
    !isSongsLoading &&
    soundkitsCount === 0 &&
    !isSoundkitsLoading
  ) {
    return (
      <div className={cx("flex flex-col items-center w-full mb-20", className)}>
        <div className="h-[50px]" />
        <Empty icon={Headphones} title={"Nothing Published Yet"} />
      </div>
    );
  }

  return (
    <Tab.Group
      onChange={(index) => onTabChange(tabs[index])}
      selectedIndex={selectedTab.id}
    >
      <div
        className={cx("flex w-full justify-between items-center", className)}
      >
        <Tab.List className="justify-start hidden lg:flex h-big">
          <Tab
            id="creator-tab-beats"
            value="Beats"
            className={({ selected }) => {
              const selectedClassName = selected
                ? "border-b-[2px] border-black outline-none px-7"
                : "px-7 text-gray";
              const hiddenClassName = beatsCount === 0 ? "hidden" : "";
              return `${selectedClassName} ${hiddenClassName}`;
            }}
          >
            Beats ({beatsCount})
          </Tab>
          <Tab
            id="creator-tab-songs"
            value={"Songs"}
            className={({ selected }) => {
              const selectedClassName = selected
                ? "border-b-[2px] border-black outline-none px-7"
                : "px-7 text-gray";
              const hiddenClassName = songsCount === 0 ? "hidden" : "";
              return `${selectedClassName} ${hiddenClassName}`;
            }}
          >
            Songs ({songsCount})
          </Tab>
          <Tab
            id="creator-tab-sounds"
            value={"Sounds"}
            className={({ selected }) => {
              const selectedClassName = selected
                ? "border-b-[2px] border-black outline-none px-7"
                : "px-7 text-gray";
              const hiddenClassName = soundkitsCount === 0 ? "hidden" : "";
              return `${selectedClassName} ${hiddenClassName}`;
            }}
          >
            Sound Kits ({soundkitsCount})
          </Tab>
        </Tab.List>

        <TitleBarMobile
          allowBack={false}
          title={`${selectedTab.name}`}
          right={
            <div className="flex items-center gap-4">
              <DropdownAlt
                id="mobile-creator-dropdown-sort-expand"
                options={
                  selectedTab.name === "All" ||
                  selectedTab.name === "Sound Kits"
                    ? sortOptions.filter((opt) => opt.id !== 4 && opt.id !== 5)
                    : sortOptions
                }
                value={sort.name}
                onItemClick={(option) => {
                  handleSortClick(option);
                }}
                className="mr-5"
              />
              <ToggleButton
                id="creator-toggle-gridList"
                value={viewType === "list"}
                onChange={toggleViewType}
                mobile
                className="relative"
              />
            </div>
          }
          className={`${size.width! < 1024 ? "flex" : "hidden"} px-3`}
        />

        {/* Grid/List toggle (desktop) */}
        <div className="items-center hidden gap-4 lg:flex">
          <DropdownAlt
            id="creator-dropdown-sort-expand"
            options={sortOptions}
            value={sort.name}
            onItemClick={(option) => {
              handleSortClick(option);
            }}
            className="hidden mr-5 lg:inline-block"
          />
          <ToggleButton
            id="creator-toggle-gridList"
            value={viewType === "list"}
            onChange={toggleViewType}
            mobile
            className="relative"
          />
        </div>
      </div>

      <Tab.Panels className={cx("block pb-normal lg:py-normal", className)}>
        {/* Beats */}
        <Tab.Panel className="flex flex-col">
          {viewType === "list" ? (
            <>
              <ResponsiveList>
                {beats?.map((track, index) => (
                  <ListTile
                    key={`Beat-List-${index}`}
                    item={track}
                    playableTrack
                    hoverEffect
                    type={isSoundkit(track) ? AudioType.kit : AudioType.track}
                    onPlay={handleItemPlay}
                  />
                ))}
                {isBeatsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <ListTileSkeleton key={key} />
                  ))}
              </ResponsiveList>
              {isBeatsLoadingMore && <ResponsiveList loading={true} />}
            </>
          ) : (
            <>
              <ResponsiveGrid>
                {beats?.map((track, index) => (
                  <GridTile
                    key={`Beat-${index}`}
                    item={track}
                    hoverEffect
                    playableTrack
                    onPlay={handleItemPlay}
                  />
                ))}
                {isBeatsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <GridTileSkeleton key={key} />
                  ))}
              </ResponsiveGrid>
              {isBeatsLoadingMore && <ResponsiveGrid loading={true} />}
            </>
          )}

          {!isBeatsLoading && (beats?.length ?? 0) < (beatsCount ?? 0) && (
            <div className="flex justify-center my-5">
              <button
                id="creator-button-viewMoreSounds"
                className="text-small rounded-full border-[1px] border-black text-black py-2 px-5"
                onClick={() => onViewMore?.("beats")}
              >
                View More
              </button>
            </div>
          )}
        </Tab.Panel>

        {/* Songs */}
        <Tab.Panel className="flex flex-col">
          {viewType === "list" ? (
            <>
              <ResponsiveList>
                {songs?.map((track, index) => (
                  <ListTile
                    key={`Song-List-${index}`}
                    item={track}
                    playableTrack
                    hoverEffect
                    type={isSoundkit(track) ? AudioType.kit : AudioType.track}
                    onPlay={handleItemPlay}
                  />
                ))}
                {isSongsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <ListTileSkeleton key={key} />
                  ))}
              </ResponsiveList>
              {isSongsLoadingMore && <ResponsiveList loading={true} />}
            </>
          ) : (
            <>
              <ResponsiveGrid>
                {songs?.map((track, index) => (
                  <GridTile
                    key={`Song-${index}`}
                    item={track}
                    hoverEffect
                    playableTrack
                    onPlay={handleItemPlay}
                  />
                ))}
                {isSongsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <GridTileSkeleton key={key} />
                  ))}
              </ResponsiveGrid>
              {isSongsLoadingMore && <ResponsiveGrid loading={true} />}
            </>
          )}

          {!isSongsLoading && (songs?.length ?? 0) < (songsCount ?? 0) && (
            <div className="flex justify-center my-5">
              <button
                id="creator-button-viewMoreSounds"
                className="text-small rounded-full border-[1px] border-black text-black py-2 px-5"
                onClick={() => onViewMore?.("songs")}
              >
                View More
              </button>
            </div>
          )}
        </Tab.Panel>

        {/* Soundkits */}
        <Tab.Panel className="flex flex-col">
          {viewType === "list" ? (
            <>
              <ResponsiveList>
                {soundkits?.map((track, index) => (
                  <ListTile
                    key={`Kit-List-${index}`}
                    item={track}
                    playableTrack
                    hoverEffect
                    type={isSoundkit(track) ? AudioType.kit : AudioType.track}
                    onPlay={handleItemPlay}
                  />
                ))}
                {isSoundkitsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <ListTileSkeleton key={key} />
                  ))}
              </ResponsiveList>
              {isSoundkitsLoadingMore && <ResponsiveList loading={true} />}
            </>
          ) : (
            <>
              <ResponsiveGrid>
                {soundkits?.map((track, index) => (
                  <GridTile
                    key={`Kit-${index}`}
                    item={track}
                    hoverEffect
                    playableTrack
                    onPlay={handleItemPlay}
                  />
                ))}
                {isSoundkitsLoading &&
                  Array.from(Array(10).keys()).map((key) => (
                    <GridTileSkeleton key={key} />
                  ))}
              </ResponsiveGrid>

              {isSoundkitsLoadingMore && <ResponsiveGrid loading={true} />}
            </>
          )}

          {isSoundkitsLoading &&
            (soundkits?.length ?? 0) < (soundkitsCount ?? 0) && (
              <div className="flex justify-center my-5">
                <button
                  id="creator-button-viewMoreSounds"
                  className="text-small rounded-full border-[1px] border-black text-black py-2 px-5"
                  onClick={() => onViewMore?.("sounds")}
                >
                  View More
                </button>
              </div>
            )}
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
};

export default CreatorsAssetsTabs;
